import { useDebouncedValue, useIntersection } from '@mantine/hooks';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import HomeIcon from '@mui/icons-material/Home';
import PlaceIcon from '@mui/icons-material/Place';
import ReplayIcon from '@mui/icons-material/Replay';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Fade,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import useAutocomplete from '@mui/material/useAutocomplete';
import ErrorAlerts from 'components/generic/ErrorAlerts';
import { IntersectionLoader } from 'components/generic/IntersectionLoader';
import { CosmosContextType } from 'providers/contexts/CosmosContext';
import { sessionsProvider } from 'providers/contexts/extensions/Sessions';
import { memo, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { bloxValues } from 'themes/constants';
import { getPrimaryBgColor, getSuccessBgColor } from 'utility/styles';
import {
  ApiErrorResponse,
  GetDefaultSiteResult,
  GetSitesParams,
  GetSitesResult,
} from 'utility/types/dataProvider';
import { hardReloadClearAuth } from '../../providers/contexts/CosmosAuthEnvContext';
import { useTranslation } from 'react-i18next';

type NoCosmosPermissionsProps = {
  activeSite: CosmosContextType['activeSite'];
  changeActiveSiteAsync: CosmosContextType['changeActiveSiteAsync'];
};

const NoCosmosPermissions = memo(
  ({ activeSite, changeActiveSiteAsync }: NoCosmosPermissionsProps) => {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
      <Box
        data-testid="no-permissions-container"
        sx={{
          m: 1,
          p: 1,
          border: `1px solid ${getPrimaryBgColor(theme, true)}`,
          bgcolor: getPrimaryBgColor(theme),
          borderRadius: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: bloxValues.xxlFontSizeRem,
            fontWeight: bloxValues.fontWeightMedium,
          }}
        >
          {t('blox.site_selector.no_permissions')}
        </Typography>

        <Typography
          sx={{
            fontSize: bloxValues.regularFontSizeRem,
            fontWeight: bloxValues.fontWeightRegular,
            color: theme.palette.text.secondary,
          }}
        >
          {t('blox.site_selector.message')}
        </Typography>

        <Typography
          sx={{
            fontSize: bloxValues.regularFontSizeRem,
            fontWeight: bloxValues.fontWeightRegular,
            color: theme.palette.text.secondary,
            mb: 2,
          }}
        >
          {t('blox.site_selector.contact')}
        </Typography>

        <NoPermissionSiteSelector
          activeSite={activeSite}
          changeActiveSiteAsync={changeActiveSiteAsync}
        />
      </Box>
    );
  },
);
NoCosmosPermissions.displayName = 'NoCosmosPermissions';

export default NoCosmosPermissions;

/**
 * This is a temporary fix for CMSADMIN-1552 as it was
 * discussed in standup that this will not be a real issue
 * once clients are fully moved over to NXT Admin.
 *
 * In the meantime adding simpler variant of the NewSiteSelector
 * to use in the the NoCosmosPerms component.
 *
 * Note: not all hooks are available at this level in the react tree...
 * eg: no useDataProvider, notify context, parts of useTheme
 * or translations may not be ready above react-admin level etc...
 * so this is a more simplified copy of the NewSiteSelector.
 */

export function normalizeSite(site?: string): string {
  if (!site) {
    return '';
  }
  if (typeof site !== 'string') {
    return '';
  }
  const normalized = site.trim().toLowerCase();
  return normalized;
}

type NoPermissionSiteSelectorProps = NoCosmosPermissionsProps;

const NoPermissionSiteSelector = memo(
  ({ activeSite, changeActiveSiteAsync }: NoPermissionSiteSelectorProps) => {
    const theme = useTheme();
    const { isAuthenticated } = useAuth();
    const [inputVal, setInputVal] = useState<string>('');
    const [debouncedInputValue] = useDebouncedValue(inputVal, 400);
    const normDbInputValue = normalizeSite(debouncedInputValue);
    const { t } = useTranslation();

    const nActiveSite = useMemo(() => normalizeSite(activeSite), [activeSite]);

    const { ref: ixRef, entry: inView } = useIntersection({
      root: null,
      threshold: 0.5,
    });

    // fetch users default site from API
    const { data: fetchedDefaultSite, error: defaultSiteError } = useQuery<
      GetDefaultSiteResult,
      ApiErrorResponse,
      string
    >({
      queryKey: ['getDefaultSite'],
      queryFn: async () => {
        try {
          const result = await sessionsProvider.getDefaultSite();
          return result;
        } catch (error) {
          console.error('[NoPerms getDefaultSite] Error:', error);
          throw error;
        }
      },
      enabled: isAuthenticated,
      select: (data) => normalizeSite(data?.data?.domain),
      refetchOnReconnect: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });

    // fetch list of domains user has access to
    const {
      data: sitesData,
      error: sitesError,
      isLoading: sitesLoading,
      isFetching: sitesFetching,
      isFetchingNextPage: sitesNextPageFetching,
      hasNextPage: sitesHasNextPage,
      fetchNextPage: sitesFetchNextPgFn,
    } = useInfiniteQuery<GetSitesResult, ApiErrorResponse>({
      queryKey: ['getSites', debouncedInputValue],
      queryFn: async ({ pageParam = '' }) => {
        try {
          const paramsWithPage: GetSitesParams = {
            ...(normDbInputValue ? { query: normDbInputValue } : {}),
            ...(pageParam ? { page: pageParam as string } : {}),
            limit: 90,
          };
          const result = await sessionsProvider.getSites(paramsWithPage);
          return result;
        } catch (error) {
          console.error('[NoPerms getSites] Error:', error);
          throw error;
        }
      },
      enabled: isAuthenticated,
      initialPageParam: '',
      getNextPageParam: (response) => response?.data?.next || undefined,
      refetchOnReconnect: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });

    const anyError = sitesError || defaultSiteError;

    const listQueryLoading = sitesNextPageFetching || sitesLoading || sitesFetching;

    // Flatten and normalize domains from infinite query pages.
    const domains = useMemo<string[]>(() => {
      if (!sitesData?.pages) {
        return [];
      }
      const nDomains = sitesData.pages
        .flatMap((page) => {
          return page?.data?.items?.map((item) => normalizeSite(item?.domain)) || [];
        })
        .filter(Boolean);
      return nDomains;
    }, [sitesData]);

    const {
      getInputProps,
      getListboxProps,
      getOptionProps,
      groupedOptions,
      getRootProps,
    } = useAutocomplete({
      options: domains,
      getOptionLabel: (option) => normalizeSite(String(option)),
      inputValue: inputVal,
      onInputChange: (event, value, reason) => {
        const nValue = normalizeSite(value);
        const isOptionSelection = reason === 'selectOption' || reason === 'reset';
        const isCurrentSite = Boolean(nValue) && nValue === nActiveSite;
        // Prevent input mui autofill if selecting current active site
        if (isCurrentSite && isOptionSelection) {
          return;
        }
        setInputVal(value);
      },
      onChange: async (event, newValue, reason, details) => {
        // Change active site if user selects a new site
        const nValue = normalizeSite(details?.option);
        if (!nValue) {
          return;
        }
        // Already on this site... no change.
        if (nValue === nActiveSite) {
          return;
        }
        // Only navigate to an option returned by the API.
        if (!domains.includes(nValue)) {
          return;
        }
        // Nav to new site
        await changeActiveSiteAsync(nValue);
      },
      autoHighlight: false,
      autoComplete: false,
      autoSelect: false,
      freeSolo: true,
      open: true,
      multiple: false,
      disableCloseOnSelect: true,
    });

    const rootProps = useMemo(() => {
      return getRootProps();
    }, [getRootProps]);

    const textfieldProps = useMemo(() => {
      return getInputProps();
    }, [getInputProps]);

    const listboxProps = useMemo(() => {
      return getListboxProps();
    }, [getListboxProps]);

    return (
      <Box
        data-testid="no-permissions-site-selector"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          p: 2,
          borderRadius: 2,
          maxWidth: '650px',
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${getPrimaryBgColor(theme, true)}`,
        }}
      >
        {anyError ? (
          <ErrorAlerts
            severity="warning"
            buttonOnClick={() => {
              hardReloadClearAuth({ type: 'reload' });
            }}
            buttonText="Reload"
            buttonIcon={<ReplayIcon />}
            sx={{
              mb: 2,
            }}
            errors={[
              {
                message: t('blox.site_selector.list_error'),
              },
            ]}
          />
        ) : null}

        <Box
          data-testid="no-permissions-input-wrapper"
          {...rootProps}
        >
          <TextField
            color="primary"
            size="small"
            label="Active Site"
            variant="outlined"
            fullWidth={true}
            autoComplete="off"
            data-testid="no-permissions-active-site-textfield"
            placeholder="Filter site list..."
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment
                    position="start"
                    sx={{
                      marginRight: '8px',
                    }}
                  >
                    <SearchIcon
                      sx={{
                        fontSize: '1.5rem',
                      }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <Fade
                    in={Boolean(inputVal)}
                    timeout={500}
                  >
                    <InputAdornment
                      position="end"
                      sx={{
                        marginLeft: '8px',
                      }}
                    >
                      {inputVal && (
                        <Tooltip
                          title="Clear"
                          disableInteractive
                        >
                          <IconButton
                            aria-label="clear"
                            onClick={() => setInputVal('')}
                            size="small"
                          >
                            <ClearRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </InputAdornment>
                  </Fade>
                ),
              },

              htmlInput: {
                ...textfieldProps,
                className: 'active-site-textfield',
              },

              inputLabel: { shrink: true },
            }}
          />
        </Box>

        <List
          dense={true}
          disablePadding={true}
          sx={{
            maxHeight: '400px',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            p: 0,
          }}
          {...listboxProps}
        >
          {Array.isArray(groupedOptions) &&
            groupedOptions.map((opt, idx) => {
              // groupedOptions MUI type is string[] | AutocompleteGroupedOption<string>
              // but MUI types are unable to infer type because of freesolo / groupby
              const optStr = normalizeSite(opt);
              const isCurrentSite = optStr === nActiveSite;
              const isDefaultSite = optStr === fetchedDefaultSite;

              const { key, ...optionProps } = getOptionProps({
                option: optStr,
                index: idx,
              });

              return (
                <ListItem
                  key={key}
                  dense={true}
                  disablePadding={true}
                  disableGutters={true}
                  data-testid="no-permissions-site-option"
                  sx={{
                    px: '8px',
                    py: '10px',
                    gap: 1,
                    backgroundColor: isCurrentSite ? getSuccessBgColor(theme) : undefined,
                    '&:hover, &:focus-within': {
                      cursor: 'pointer',
                      backgroundColor: isCurrentSite
                        ? getSuccessBgColor(theme, true)
                        : theme.palette.action.hover,
                    },
                  }}
                  {...optionProps}
                >
                  <Tooltip
                    title={isCurrentSite ? 'Current Site' : undefined}
                    disableInteractive={true}
                    arrow={true}
                  >
                    <PlaceIcon
                      sx={{
                        display: isCurrentSite ? 'block' : 'none',
                        borderRadius: 1,
                        bgcolor: theme.palette.success.dark,
                        p: '2px',
                        fontSize: bloxValues.iconSizeRegular,
                        color: theme.palette.primary.contrastText,
                      }}
                    />
                  </Tooltip>
                  <Tooltip
                    title={isDefaultSite ? 'Default Site' : undefined}
                    disableInteractive={true}
                    arrow={true}
                  >
                    <HomeIcon
                      sx={{
                        display: isDefaultSite ? 'block' : 'none',
                        borderRadius: 1,
                        bgcolor: theme.palette.info.dark,
                        p: '2px',
                        fontSize: bloxValues.iconSizeRegular,
                        color: theme.palette.primary.contrastText,
                      }}
                    />
                  </Tooltip>
                  {optStr}
                </ListItem>
              );
            })}
          <IntersectionLoader
            polymorph={'li'}
            hasNextPage={sitesHasNextPage}
            isFetching={listQueryLoading}
            fetchNextPage={sitesFetchNextPgFn}
            ixRef={ixRef}
            entry={inView}
            endOfResultsMessage={domains.length ? 'Showing all sites' : 'No sites found'}
            compact={true}
            sx={{
              m: 1,
              width: 'auto',
            }}
          />
        </List>
      </Box>
    );
  },
);
NoPermissionSiteSelector.displayName = 'NoPermissionSiteSelector';
